import { MessageDelivery, MessageDeliveryStatus, MessageType, Thread, ThreadStatus } from '../../types';

export interface InboxChild {
  id: string;
  displayName: string;
  firstName: string;
  status: 'PENDING_START' | 'ACTIVE' | 'LEFT';
  locationId: string;
}
export interface ConversationRow {
  threadId: string;
  studentId: string;
  childName: string;
  subject: string;
  lastLine: string;
  lastMessageAt: string;
  unread: boolean;
  status: ThreadStatus;
}
export interface SendRow {
  delivery: MessageDelivery;
  repliedFor: Array<{ studentId: string; childName: string; threadId: string }>;
  canReplyFor: Array<{ id: string; displayName: string; firstName: string }>;
}

const firstName = (c: InboxChild) => c.firstName || c.displayName;

/**
 * The family inbox: conversations (one row per thread, across every child)
 * and sends from the service (announcements and per-child notes, replyable or
 * not). Thread-post deliveries never become rows of their own; they only mark
 * the conversation unread — or, when their thread is missing from
 * threadsByChild, stand in for it as a synthesised conversation row. A
 * delivery that names a thread but no child (rows
 * from before studentId was stored) cannot address a thread route, so it is
 * listed as a plain send with no reply state.
 */
export function buildFamilyInbox(input: {
  children: InboxChild[];
  deliveries: MessageDelivery[];
  threadsByChild: Record<string, Thread[]>;
}): { conversations: ConversationRow[]; sends: SendRow[] } {
  const { children, deliveries, threadsByChild } = input;
  const childById = new Map(children.map((c) => [c.id, c]));
  // A thread post is a PARENT_DM that names a thread and was never a replyable
  // send: post-thread-message stores no repliesAllowed, whereas a send the
  // parent replied to keeps its repliesAllowed and gains threadId/studentId
  // (deliveryService.attachThread). Without this distinction a replied-to
  // announcement would disappear from the inbox.
  const isThreadPost = (d: MessageDelivery) =>
    d.type === MessageType.PARENT_DM && Boolean(d.threadId && d.studentId) && d.repliesAllowed === undefined;
  // Any unread delivery naming a thread marks that conversation unread — the
  // same set openConversation marks read, so opening it always clears the dot.
  const unreadThreadIds = new Set(
    deliveries.filter((d) => d.threadId && d.status === MessageDeliveryStatus.UNREAD).map((d) => d.threadId as string)
  );
  const allThreads = children.flatMap((c) => threadsByChild[c.id] ?? []);

  const conversations: ConversationRow[] = allThreads
    .map((t) => {
      const child = childById.get(t.studentId);
      return {
        threadId: t.id,
        studentId: t.studentId,
        childName: child ? firstName(child) : 'Your child',
        subject: t.originTitle ?? 'Conversation with staff',
        lastLine: `${t.lastMessageBy === 'PARENT' ? 'You' : 'Staff'}: ${t.lastMessagePreview}`,
        lastMessageAt: t.lastMessageAt,
        unread: unreadThreadIds.has(t.id),
        status: t.status,
      };
    });

  // A staff post whose thread is missing from the per-child lists (that list
  // failed to load, or a co-parent started the thread after it loaded) would
  // otherwise be counted by the Inbox badge yet listed nowhere. Synthesise one
  // row per such thread from its newest post.
  const knownThreadIds = new Set(allThreads.map((t) => t.id));
  const orphanPostByThread = new Map<string, MessageDelivery>();
  for (const d of deliveries) {
    if (!isThreadPost(d) || knownThreadIds.has(d.threadId as string)) continue;
    const newest = orphanPostByThread.get(d.threadId as string);
    if (!newest || d.createdAt > newest.createdAt) orphanPostByThread.set(d.threadId as string, d);
  }
  for (const [threadId, d] of orphanPostByThread) {
    const child = childById.get(d.studentId as string);
    conversations.push({
      threadId,
      studentId: d.studentId as string,
      childName: child ? firstName(child) : d.studentName?.trim().split(/\s+/)[0] || 'Your child',
      subject: 'Conversation with staff',
      lastLine: `Staff: ${d.bodyPreview}`,
      lastMessageAt: d.createdAt,
      unread: unreadThreadIds.has(threadId),
      // Only staff post into a parent's inbox, so the thread has been answered.
      status: ThreadStatus.ANSWERED,
    });
  }
  conversations.sort((a, b) => b.lastMessageAt.localeCompare(a.lastMessageAt));

  const isSend = (d: MessageDelivery) =>
    !isThreadPost(d) && (d.type === MessageType.ANNOUNCEMENT || d.type === MessageType.PARENT_DM);

  const sends: SendRow[] = deliveries
    .filter(isSend)
    .map((d) => {
      const repliedFor = allThreads
        .filter((t) => t.originMessageId === d.messageId)
        .map((t) => {
          const child = childById.get(t.studentId);
          return { studentId: t.studentId, childName: child ? firstName(child) : 'Your child', threadId: t.id };
        });
      const repliedIds = new Set(repliedFor.map((r) => r.studentId));
      // Same scope rule as MessageReplyBox: a note names its child; a location
      // send narrows by location; an org-wide send offers every child. The
      // server is the authority on class scope.
      let scope = children.filter((c) => c.status !== 'LEFT');
      if (d.type === MessageType.PARENT_DM) scope = scope.filter((c) => c.id === d.studentId);
      else if (d.targetLocationId) scope = scope.filter((c) => c.locationId === d.targetLocationId);
      const canReplyFor = d.repliesAllowed
        ? scope.filter((c) => !repliedIds.has(c.id)).map((c) => ({ id: c.id, displayName: c.displayName, firstName: firstName(c) }))
        : [];
      return { delivery: d, repliedFor, canReplyFor };
    })
    .sort((a, b) => b.delivery.createdAt.localeCompare(a.delivery.createdAt));

  return { conversations, sends };
}

// Name lists are capped so a family with many children still gets a one-line
// reply state: up to three replied names, up to two still-to-reply names.
const MAX_REPLIED_NAMES = 3;
const MAX_REMAINING_NAMES = 2;

const joinNames = (names: string[]) => {
  if (names.length > MAX_REPLIED_NAMES) {
    return `${names.slice(0, MAX_REPLIED_NAMES - 1).join(', ')} and ${names.length - (MAX_REPLIED_NAMES - 1)} more`;
  }
  return names.length <= 1 ? names.join('') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
};

const orNames = (names: string[]) =>
  names.length > MAX_REMAINING_NAMES
    ? `${names.slice(0, MAX_REMAINING_NAMES).join(' or ')} and ${names.length - MAX_REMAINING_NAMES} more`
    : names.join(' or ');

/** The reply-state line under a send: null when the send cannot be answered. */
export function replyStateLine(send: SendRow): string | null {
  if (!send.delivery.repliesAllowed) return null;
  const replied = send.repliedFor.map((r) => r.childName);
  const remaining = send.canReplyFor.map((c) => c.firstName);
  if (replied.length === 0 && remaining.length === 0) return null;
  if (replied.length === 0) return 'Reply';
  if (remaining.length === 0) return `Replied for ${joinNames(replied)}`;
  return `Replied for ${joinNames(replied)} · reply for ${orNames(remaining)}`;
}
